/**
 * Contexto de métricas del dashboard (F2) — lógica sin JSX. Desacopla la UI
 * (`DashboardPage`) de la fuente de datos: en producción se inyecta un
 * `subscribe` que combina los watchers RTDB en vivo; en tests, un fake. Sin
 * provider, el hook devuelve `null` (carga → la UI muestra "—").
 *
 * El componente provider vive en `MetricsProvider.tsx` (regla react-refresh:
 * un `.tsx` solo exporta componentes).
 */
import type { DriverDetailsRtdb, ReserveDetailsRtdb, RideDetailsRtdb } from '@ibercar/rtdb-contracts';
import type { Database } from 'firebase/database';
import { createContext, useContext } from 'react';
import { calculateAdminMetrics, type AdminMetrics } from './metrics';
import { watchActiveReserves, watchFleetDrivers, watchRecentRides } from './watchers';

/** `subscribe`: registra un callback de métricas y devuelve la función de baja. */
export type MetricsSubscribe = (onMetrics: (metrics: AdminMetrics) => void) => () => void;

export const MetricsContext = createContext<AdminMetrics | null>(null);

/** Métricas actuales del dashboard, o `null` si aún no hay datos / sin provider. */
export function useDashboardMetrics(): AdminMetrics | null {
  return useContext(MetricsContext);
}

/** Medianoche local de `nowMs` menos 1h (ventana del watcher de rides recientes). */
function ridesCutoffIso(nowMs: number): string {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  return new Date(d.getTime() - 60 * 60 * 1000).toISOString();
}

/**
 * Ensambla los tres watchers RTDB en vivo y recalcula las métricas en cuanto
 * cualquiera emite (paridad con `adminMetricsProvider`). Live, no unit-tested.
 */
export function createLiveMetricsSubscribe(
  db: Database,
  now: () => number = () => Date.now(),
): MetricsSubscribe {
  return (onMetrics) => {
    let drivers: DriverDetailsRtdb[] = [];
    let rides: RideDetailsRtdb[] = [];
    let reserves: ReserveDetailsRtdb[] = [];
    const startedAt = now();

    const recompute = (): void => {
      onMetrics(calculateAdminMetrics({ rides, reserves, drivers, now: now() }));
    };

    const offDrivers = watchFleetDrivers(db, (d) => {
      drivers = d;
      recompute();
    });
    const offRides = watchRecentRides(db, ridesCutoffIso(startedAt), (r) => {
      rides = r;
      recompute();
    });
    const offReserves = watchActiveReserves(db, (r) => {
      reserves = r;
      recompute();
    });

    return () => {
      offDrivers();
      offRides();
      offReserves();
    };
  };
}
